import type { MouseEvent } from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/lib/theme';

/** The View Transitions API isn't in the standard TS lib yet — narrow it
 *  with a structural type so we can call it without `any`. */
type StartViewTransitionDoc = Document & {
  startViewTransition?: (cb: () => void) => { ready: Promise<void> };
};

/**
 * One-tap dark/light toggle with a circular reveal of the new theme.
 *
 * First visit follows the user's OS preference (the provider stores
 * `theme: 'system'` until explicitly changed). Clicking toggles between
 * `light` and `dark` based on the resolved theme, and the provider
 * persists the choice to localStorage.
 *
 * When the browser supports `document.startViewTransition`, we wrap the
 * theme swap in it and animate the new snapshot's `clip-path` from a
 * zero-radius circle at the click point to a circle large enough to cover
 * the viewport. Browsers without the API (Firefox today) simply get an
 * instant swap — there is no JS-side crossfade fallback because the CSS
 * variable change already produces a sharp, low-latency repaint.
 */
export function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const { t } = useTranslation('common');
  const next = resolvedTheme === 'dark' ? 'light' : 'dark';
  const label = t('theme.toggleTo', {
    defaultValue: 'Switch to {{mode}} mode',
    mode: t(`theme.${next}`),
  });

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    const doc = document as StartViewTransitionDoc;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!doc.startViewTransition || reduce) {
      setTheme(next);
      return;
    }

    // Origin of the reveal — centre on the toggle button, not the precise
    // pixel clicked, so keyboard activation (which reports 0,0) and small
    // mouse jitters still feel intentional.
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;

    const transition = doc.startViewTransition(() => setTheme(next));

    transition.ready.then(() => {
      const endRadius = Math.hypot(
        Math.max(cx, window.innerWidth - cx),
        Math.max(cy, window.innerHeight - cy),
      );
      document.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${cx}px ${cy}px)`,
            `circle(${endRadius}px at ${cx}px ${cy}px)`,
          ],
        },
        {
          duration: 520,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          pseudoElement: '::view-transition-new(root)',
        },
      );
    });
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={label}
      title={label}
      onClick={handleClick}
    >
      <Sun className="h-4 w-4 scale-100 rotate-0 transition-all dark:scale-0 dark:-rotate-90" />
      <Moon className="absolute h-4 w-4 scale-0 rotate-90 transition-all dark:scale-100 dark:rotate-0" />
    </Button>
  );
}
